import throttle from 'lodash.throttle';
import { userListsApi } from '~/Providers/Api';
import { error } from '~/Redux/Modules/Notifications';
import { parseErrors } from '~/Redux/Helpers';
import Store from '~/Redux/Store';
import clevertapProvider, { ListCreateEvent } from '~/Providers/CleverTap';
import { initializeOnboarding } from '../Onboarding/helpers';
import { sameListId } from './helpers';

import {
  FETCH,
  FETCH_SUCCESS,
  FETCH_FAILURE,
  FETCH_ONE,
  FETCH_ONE_SUCCESS,
  FETCH_ONE_FAILURE,
  UPDATE,
  UPDATE_SUCCESS,
  UPDATE_FAILURE,
  DELETE,
  DELETE_SUCCESS,
  DELETE_FAILURE,
  STORE,
  STORE_SUCCESS,
  STORE_FAILURE,
  CLEAR_LISTS,
  SET_CURRENT,
  UNSET_CURRENT,
  START_UPDATING,
  FINISH_UPDATING,
  SET_LAST_UPDATE,
  SET_QUEUE,
  TOGGLE_RECOVER,
} from './constants';

export const getUserLists = () => async dispatch => {
  dispatch({ type: FETCH });

  let perPage = 1;
  let page = 1;
  let allLists = [];
  let hasMorePages = true;

  try {
    //Step 1: Try fetching all lists at once
    let response = await userListsApi.fetch();
    dispatch({ type: FETCH_SUCCESS, data: response.data });
    if (response.data) {
      dispatch(setCurrent(response.data[0]));
    }
    return response;
  } catch (err) {
    console.log('Initial fetch failed. Switching to paginated fetching.', err);

    // Step 2: Fallback to pagination (one list per page)
    while (hasMorePages) {
      try {
        let response = await userListsApi.fetchWithPagination(page, perPage);

        if (response.data) {
          allLists = [...allLists, ...response.data.data];

          if (page === 1) {
            dispatch(setCurrent(response.data.data[0]));
          }

          hasMorePages = response.data.data.length === perPage; // Check if more pages exist
          page++; // Advance to the next page
        } else {
          hasMorePages = false; // Stop if no lists are found
        }
      } catch (err) {
        console.log(`Error fetching page ${page}:`, err);
        break;
      }
    }
    dispatch({ type: FETCH_SUCCESS, data: allLists });
  }
};

export const getDeletedUserLists = () => async dispatch => {
  dispatch({ type: FETCH });
  try {
    let response = await userListsApi.fetchDeleted();
    dispatch({ type: FETCH_SUCCESS, data: response.data });
    if (response.data) {
      dispatch(setCurrent(response.data[0]));
    }
    return response;
  } catch (err) {
    dispatch({ type: FETCH_FAILURE, error: err });
    dispatch(error('Unable to fetch lists', parseErrors(err)));
  }
};

export const recoverUserList = (id, toggle) => async dispatch => {
  dispatch({ type: FETCH_ONE, id });
  try {
    let response = await userListsApi.recoverDeleted(id);
    dispatch({ type: FETCH_ONE_SUCCESS, data: response.data, id });
    if (response.data) {
      dispatch(getUserLists());
      dispatch(setCurrent(response.data));
      dispatch(toggleRecover(toggle));
    }
    return response;
  } catch (err) {
    dispatch({ type: FETCH_ONE_FAILURE, error: err, id });
    dispatch(error('Unable to recover list', parseErrors(err)));
  }
};

export const getUserList = id => async dispatch => {
  dispatch({ type: FETCH_ONE, id });
  try {
    let response = await userListsApi.fetchOne(id);
    dispatch({ type: FETCH_ONE_SUCCESS, data: response.data, id });
    return response;
  } catch (err) {
    dispatch({ type: FETCH_ONE_FAILURE, error: err, id });
    dispatch(error('Unable to fetch list', parseErrors(err)));
  }
};

export const createList = data => async dispatch => {
  dispatch({ type: STORE });
  try {
    let response = await userListsApi.store(data);
    clevertapProvider.logEvent(new ListCreateEvent());
    dispatch({ type: STORE_SUCCESS, data: response.data });
    if (response.data) {
      dispatch(setCurrent(response.data));
    }
    dispatch(initializeOnboarding());
    return response;
  } catch (err) {
    dispatch({ type: STORE_FAILURE, error: err });
    dispatch(error('List was not created', parseErrors(err)));
  }
};

export const deleteList = data => async dispatch => {
  dispatch({ type: DELETE });
  try {
    let response = await userListsApi.delete(data);
    dispatch({ type: DELETE_SUCCESS, data: response.data });
    return response;
  } catch (err) {
    dispatch({ type: DELETE_FAILURE, error: err });
    dispatch(error('List was not deleted', parseErrors(err)));
  }
};

export const setCurrent = data => dispatch => {
  dispatch({ type: SET_CURRENT, data });
};

export const unsetCurrent = () => dispatch => {
  dispatch({ type: UNSET_CURRENT });
};

export const clearList = () => dispatch => {
  dispatch({ type: CLEAR_LISTS });
};

export const toggleRecover = toggle => {
  return { type: TOGGLE_RECOVER, payload: !toggle };
};

const updateThrottlers = {};

export const updateList = data => dispatch => {
  const {
    UserLists: {
      updates: { isUpdating, queue },
    },
  } = Store.getState();

  const toUpdate = { ...data };

  dispatch({
    type: UPDATE,
    data: toUpdate,
  });

  if (isUpdating) {
    // Merge rather than replace. A queued entry used to be dropped wholesale by the next update
    // for the same list, so a partial one -- a last_opened_at bump, a rename -- discarded a body
    // write waiting behind it, and autosave would not retry because lastUpdate already matched.
    // Safe to merge: every queued entry is client-generated, so later fields are newer (ARTFLT-1587).
    const queued = queue.find(update => sameListId(update.id, toUpdate.id));
    dispatch({
      type: SET_QUEUE,
      queue: [
        ...queue.filter(update => !sameListId(update.id, toUpdate.id)),
        { ...queued, ...toUpdate },
      ],
    });
  } else {
    dispatch(listUpdate(toUpdate));
  }
};

const listUpdate = data => dispatch => {
  const {
    UserLists: {
      updates: { lastUpdates },
    },
  } = Store.getState();
  let toUpdate = data;

  const identifier = data.id;
  if (
    lastUpdates[identifier] &&
    (!data.updated_at || lastUpdates[identifier].updated_at > data.updated_at)
  ) {
    toUpdate = {
      ...data,
      updated_at: lastUpdates[identifier].updated_at,
    };
  }

  dispatch({ type: START_UPDATING });
  userListsApi
    .update(toUpdate)
    .then(result => {
      if (result && result.data) {
        dispatch({ type: SET_LAST_UPDATE, data: result.data, original: toUpdate });
        dispatch({ type: UPDATE_SUCCESS, data: { ...result.data, confirm: toUpdate.confirm } });
      }
      return result;
    })
    .then(result => {
      const {
        UserLists: {
          updates: { queue },
        },
      } = Store.getState();

      if (!queue.length) {
        dispatch({ type: FINISH_UPDATING });
        return;
      }

      let [nextUpdate, ...rest] = queue;
      dispatch({ type: SET_QUEUE, queue: rest });
      if (result && result.data && nextUpdate.id === result.data.id) {
        nextUpdate = { ...nextUpdate, updated_at: result.data.updated_at };
      }
      dispatch(listUpdate(nextUpdate));
    })
    .catch(err => {
      dispatch({ type: UPDATE_FAILURE, error: err });
      updateThrottlers[data.id] =
        updateThrottlers[data.id] ||
        throttle(
          err => {
            dispatch(
              error(
                'List was not updated',
                err.status === 422
                  ? 'The list you are trying to update is out of date. Please wait a moment.'
                  : parseErrors(err)
              )
            );
          },
          15000,
          { trailing: false }
        );
      updateThrottlers[data.id](err);
      dispatch(getUserList(data.id))
        .then(response => {
          // getUserList reports its own failure and resolves with nothing, e.g. for a list that
          // has been deleted; there is then no fresh copy to put back.
          if (!response) return;
          dispatch({ type: UNSET_CURRENT });
          dispatch({ type: SET_CURRENT, data: response.data });
        })
        .finally(() => {
          const {
            UserLists: {
              updates: { queue },
            },
          } = Store.getState();

          let updatedQueue = queue.filter(item => item.id !== data.id);
          if (updatedQueue.length) {
            let [nextUpdate, ...rest] = updatedQueue;
            dispatch({ type: SET_QUEUE, queue: rest });
            dispatch(listUpdate(nextUpdate));
          } else {
            dispatch({ type: SET_QUEUE, queue: [] });
            dispatch({ type: FINISH_UPDATING });
          }
        });
    });
};
